'use client';

import { useTranslations } from 'next-intl';
import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';

import { Button } from '@/components/ui/button';
import { frontEndRoutes } from '@/constants/routes';

type Props = {
  children: ReactNode;
  fallback?: ReactNode;
  translations: {
    title: string;
    description: string;
    tryAgain: string;
    goToSignIn: string;
  };
};

type State = {
  hasError: boolean;
  error?: Error;
};

export class AuthErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to monitoring service if needed
    if (process.env.NODE_ENV === 'development') {
      console.error('Auth Error Boundary caught:', error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
    // Force refresh the page
    window.location.reload();
  };

  handleNavigateToSignIn = () => {
    window.location.href = frontEndRoutes.auth.signIn;
  };

  render() {
    if (this.state.hasError === true) {
      if (this.props.fallback !== undefined && this.props.fallback !== null) {
        return this.props.fallback;
      }

      const { translations } = this.props;

      return (
        <div className="grid min-h-screen place-items-center bg-background px-4">
          <div className="mx-auto flex max-w-[480px] flex-col items-center gap-4 py-16 text-center">
            <h1 className="type-headline-m tracking-[-0.01em] text-foreground">
              {translations.title}
            </h1>
            <p className="type-body-m leading-relaxed text-on-surface-variant">
              {translations.description}
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error !== undefined && (
              <pre className="w-full max-w-full overflow-auto rounded-md border border-outline-variant bg-surface-2 px-3 py-2 text-left font-mono type-body-s text-on-surface-variant">
                {this.state.error.message}
              </pre>
            )}

            <div className="mt-2 flex flex-wrap justify-center gap-3">
              <Button
                variant="primary"
                onClick={this.handleReset}
              >
                {translations.tryAgain}
              </Button>
              <Button
                variant="outline"
                onClick={this.handleNavigateToSignIn}
              >
                {translations.goToSignIn}
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Wrapper component that provides translations to AuthErrorBoundary
 */
export function AuthErrorBoundaryWithTranslations({ children, fallback }: Omit<Props, 'translations'>) {
  const translate = useTranslations();

  const translations = {
    title: translate('auth.errorBoundary.title'),
    description: translate('auth.errorBoundary.description'),
    tryAgain: translate('auth.errorBoundary.tryAgain'),
    goToSignIn: translate('auth.errorBoundary.goToSignIn'),
  };

  return (
    <AuthErrorBoundary translations={translations} fallback={fallback}>
      {children}
    </AuthErrorBoundary>
  );
}
